// Wellcam Auth — Shopify Customer Account API OAuth2 PKCE flow.
//
// Esposto come window.WellcamAuth con API:
//   WellcamAuth.login()           → kick-off OAuth: genera PKCE, redirect a Shopify
//   WellcamAuth.handleCallback()  → da chiamare nella pagina /pages/auth-callback
//   WellcamAuth.logout()          → revoca token + redirect a Shopify logout
//   WellcamAuth.getAccessToken()  → token corrente (refresh-on-the-fly se scaduto)
//   WellcamAuth.isLoggedIn()      → true se c'è un token valido in sessionStorage
//   WellcamAuth.authedFetch(url, init?) → wrapper fetch con Authorization: Bearer
//
// Storage:
//   sessionStorage['wc_auth.access_token']   = JWT access token (~30 min TTL)
//   sessionStorage['wc_auth.refresh_token']  = refresh token (long-lived)
//   sessionStorage['wc_auth.expires_at']     = unix ms timestamp di scadenza
//   sessionStorage['wc_auth.id_token']       = id_token (per logout hint)
//   sessionStorage['wc_auth.code_verifier']  = PKCE verifier (durante flow login)
//   sessionStorage['wc_auth.state']          = CSRF state (durante flow login)
//   sessionStorage['wc_auth.return_url']     = URL post-login redirect target
//
// Config: window.WELLCAM_AUTH_CONFIG = { shopId, clientId, redirectUri, scope }
//   shopId        = SHOPIFY_SHOP_ID (numerico, no GID)
//   clientId      = Customer Account API client_id (= CUSTOMER_ACCOUNT_API_AUDIENCE backend)
//   redirectUri   = es. https://wellcam-staging.myshopify.com/pages/auth-callback
//   scope         = "openid email customer-account-api:full" (default)

(function() {
  // Token storage: localStorage. Originariamente sessionStorage (più sicuro,
  // scompare a chiusura tab) ma SAFARI ITP + alcune versioni di Chrome
  // wipano sessionStorage dopo redirect cross-origin a shopify.com per il
  // login OAuth → tokens persi → loop di redirect login infinito.
  // localStorage è più resiliente. La sicurezza è comunque garantita dal
  // TTL del JWT (~30 min) + refresh_token revocabile lato Shopify.
  const STORE = (() => {
    try { localStorage.setItem('__wc_probe', '1'); localStorage.removeItem('__wc_probe'); return localStorage; }
    catch { return sessionStorage; }
  })();
  // PKCE temp storage: stesso store (localStorage) per gli stessi motivi.
  const PKCE_STORE = STORE;
  const KEY_ACCESS = 'wc_auth.access_token';
  const KEY_REFRESH = 'wc_auth.refresh_token';
  const KEY_EXPIRES = 'wc_auth.expires_at';
  const KEY_ID = 'wc_auth.id_token';
  const KEY_VERIFIER = 'wc_auth.code_verifier';
  const KEY_STATE = 'wc_auth.state';
  const KEY_RETURN = 'wc_auth.return_url';

  function cfg() {
    const c = window.WELLCAM_AUTH_CONFIG || {};
    if (!c.shopId || !c.clientId || !c.redirectUri) {
      throw new Error('WellcamAuth: config mancante. Definisci window.WELLCAM_AUTH_CONFIG con shopId, clientId, redirectUri.');
    }
    // Shopify Customer Account API è una "public client": accetta PKCE puro
    // senza client_secret (verificato via test al token endpoint che ha CORS
    // abilitato per i domini di storefront whitelisted).
    return {
      shopId: c.shopId,
      clientId: c.clientId,
      redirectUri: c.redirectUri,
      scope: c.scope || 'openid email customer-account-api:full',
      authzUrl: c.authzUrl || `https://shopify.com/authentication/${c.shopId}/oauth/authorize`,
      tokenUrl: c.tokenUrl || `https://shopify.com/authentication/${c.shopId}/oauth/token`,
      logoutUrl: c.logoutUrl || `https://shopify.com/authentication/${c.shopId}/logout`,
    };
  }

  // ===== PKCE helpers =====
  function b64url(buf) {
    const bytes = new Uint8Array(buf);
    let str = '';
    for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  async function sha256(input) {
    const enc = new TextEncoder().encode(input);
    const hash = await crypto.subtle.digest('SHA-256', enc);
    return b64url(hash);
  }
  function rand(len) {
    const arr = new Uint8Array(len);
    crypto.getRandomValues(arr);
    return b64url(arr);
  }

  // ===== Login: redirect a Shopify =====
  // opts: { loginHint?: string (email per pre-fill su Shopify), prompt?: string }
  async function login(returnUrl, opts) {
    const c = cfg();
    opts = opts || {};
    const verifier = rand(64);
    const challenge = await sha256(verifier);
    const state = rand(32);
    PKCE_STORE.setItem(KEY_VERIFIER, verifier);
    PKCE_STORE.setItem(KEY_STATE, state);
    PKCE_STORE.setItem(KEY_RETURN, returnUrl || (window.location.pathname + window.location.search + window.location.hash) || '/pages/area-cliente');
    const params = new URLSearchParams({
      client_id: c.clientId,
      redirect_uri: c.redirectUri,
      response_type: 'code',
      scope: c.scope,
      state,
      code_challenge: challenge,
      code_challenge_method: 'S256',
    });
    if (opts.loginHint) params.set('login_hint', opts.loginHint);
    if (opts.prompt) params.set('prompt', opts.prompt);
    window.location.assign(`${c.authzUrl}?${params.toString()}`);
  }

  // ===== Callback: scambia code → token =====
  async function handleCallback() {
    const c = cfg();
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const stateRet = url.searchParams.get('state');
    const errorParam = url.searchParams.get('error');
    if (errorParam) {
      throw new Error(`Shopify ha risposto con errore: ${errorParam} — ${url.searchParams.get('error_description') || ''}`);
    }
    if (!code) throw new Error('Codice di autorizzazione mancante in URL');

    const stateExpected = PKCE_STORE.getItem(KEY_STATE);
    if (!stateExpected || stateExpected !== stateRet) {
      throw new Error('State non corrispondente — possibile CSRF, login annullato');
    }
    const verifier = PKCE_STORE.getItem(KEY_VERIFIER);
    if (!verifier) throw new Error('PKCE verifier mancante — login scaduto');

    // Token exchange: PRIMA via proxy backend (/api/auth/shopify/token — su Safari le
    // chiamate al NOSTRO dominio riescono, mentre la POST diretta a shopify.com può
    // appendersi per ITP → login che "non completava mai" = loop Riaccedi su mobile),
    // FALLBACK al diretto storico se il proxy non risponde.
    let j = null;
    const apiOrigin = (window.WELLCAM_AUTH_CONFIG || {}).apiOrigin;
    if (apiOrigin) {
      try {
        const ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
        const timer = ctrl ? setTimeout(() => { try { ctrl.abort(); } catch (e) {} }, 8000) : null;
        const pr = await fetch(apiOrigin + '/api/auth/shopify/token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code, code_verifier: verifier, redirect_uri: c.redirectUri }),
          signal: ctrl ? ctrl.signal : undefined,
        });
        if (timer) clearTimeout(timer);
        if (pr.ok) j = await pr.json();
      } catch (e) { /* proxy non raggiungibile → fallback diretto */ }
    }
    if (!j) {
      // Fallback: exchange diretto a Shopify (PKCE puro, public client) — path storico.
      const body = new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        code_verifier: verifier,
        client_id: c.clientId,
        redirect_uri: c.redirectUri,
      });
      const r = await fetch(c.tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!r.ok) {
        const t = await r.text().catch(() => '');
        throw new Error(`Token exchange fallito (HTTP ${r.status}): ${t.slice(0, 300)}`);
      }
      j = await r.json();
    }
    saveTokens(j);

    // Pulisci PKCE temp (localStorage)
    PKCE_STORE.removeItem(KEY_VERIFIER);
    PKCE_STORE.removeItem(KEY_STATE);

    // Redirect a return_url (o /pages/area-cliente).
    // PONTE SESSIONE STOREFRONT: il token exchange dà il JWT (per le API) ma NON
    // crea la sessione storefront Shopify (Liquid `customer`), che serve alle
    // dashboard renderizzate server-side. Se la pagina callback segnala sessione
    // assente (window.__WC_HAS_SESSION === false), passiamo da
    // /customer_authentication/login: la sessione shopify.com è appena stata
    // creata dall'OTP → hop silenzioso che stabilisce la sessione storefront e
    // TORNA a `ret` (NB: /account/login invece parcheggia sul profilo Shopify
    // e ignora checkout_url/return_url — verificato empiricamente).
    const ret = PKCE_STORE.getItem(KEY_RETURN) || '/pages/area-cliente';
    PKCE_STORE.removeItem(KEY_RETURN);
    if (window.__WC_HAS_SESSION === false) {
      // Ponte sessione storefront prioritario; il cookie durevole si stabilirà al
      // prossimo page-load (installFetchOverride → establishSessionCookie).
      window.location.replace('/customer_authentication/login?locale=it&return_to=' + encodeURIComponent(ret));
      return;
    }
    // Cookie di sessione durevole col token appena ottenuto (momento più affidabile).
    // Su Safari, se il Set-Cookie via fetch viene ignorato, establishSessionCookie fa
    // il redirect top-level /session/redeem che TORNA direttamente a `ret` → stop qui.
    try {
      const hopped = await establishSessionCookie(ret);
      if (hopped) return;
    } catch (e) {}
    window.location.replace(ret);
  }

  function saveTokens(tok) {
    if (tok.access_token) STORE.setItem(KEY_ACCESS, tok.access_token);
    if (tok.refresh_token) STORE.setItem(KEY_REFRESH, tok.refresh_token);
    if (tok.id_token) STORE.setItem(KEY_ID, tok.id_token);
    if (tok.expires_in) STORE.setItem(KEY_EXPIRES, String(Date.now() + (Number(tok.expires_in) - 30) * 1000));
  }

  // ===== Refresh on-the-fly =====
  let refreshPromise = null;

  // Refresh DIRETTO a shopify.com (public client, PKCE). Path storico → fallback.
  // Su Safari questa POST cross-origin può APPENDERSI (ITP): per questo ora è il
  // fallback e proviamo PRIMA il proxy backend (refreshViaProxy).
  function refreshDirect(rt, c) {
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: rt,
      client_id: c.clientId,
    });
    const _ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    // 7s: su mobile la POST a shopify.com è spesso LENTA ma valida; un abort troppo
    // aggressivo faceva fallire refresh legittimi.
    const _timer = _ctrl ? setTimeout(() => { try { _ctrl.abort(); } catch (e) {} }, 7000) : null;
    return fetch(c.tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
      signal: _ctrl ? _ctrl.signal : undefined,
    })
      .then(async (r) => {
        if (!r.ok) {
          const e = new Error(`Refresh diretto fallito (HTTP ${r.status})`);
          // SOLO 400/401 = refresh_token invalido/revocato → sessione morta.
          // 429/5xx = problema transitorio del server → NON invalidare la sessione.
          e.invalidGrant = (r.status === 400 || r.status === 401);
          e.transient = !e.invalidGrant;
          throw e;
        }
        const j = await r.json();
        saveTokens(j);
        return j.access_token;
      })
      .catch((err) => {
        // abort (timeout) o errore di rete = transitorio: non deve azzerare la sessione.
        if (err && !err.invalidGrant && !err.transient) err.transient = true;
        throw err;
      })
      .finally(() => { if (_timer) clearTimeout(_timer); });
  }

  // Refresh via BACKEND proxy (server→shopify.com). Su Safari le chiamate al NOSTRO
  // backend funzionano, mentre la POST diretta a shopify.com si appende (ITP). Ritorna
  // l'access_token; lancia con .invalidGrant SOLO se Shopify dice esplicitamente
  // invalid_grant (token morto). Ogni altro esito → .transient (→ fallback diretto).
  async function refreshViaProxy(rt) {
    const apiOrigin = (window.WELLCAM_AUTH_CONFIG || {}).apiOrigin;
    if (!apiOrigin) { const e = new Error('no apiOrigin'); e.transient = true; throw e; }
    const _ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    const _timer = _ctrl ? setTimeout(() => { try { _ctrl.abort(); } catch (e) {} }, 8000) : null;
    let r;
    try {
      r = await fetch(apiOrigin + '/api/auth/shopify/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: rt }),
        signal: _ctrl ? _ctrl.signal : undefined,
      });
    } catch (netErr) {
      const e = new Error('proxy net error'); e.transient = true; throw e;
    } finally { if (_timer) clearTimeout(_timer); }
    if (r.ok) {
      const j = await r.json();
      saveTokens(j);
      return j.access_token;
    }
    // Non-200: distingui invalid_grant (token davvero morto) da problemi di config/altro.
    let detail = '';
    try { detail = JSON.stringify(await r.json()); } catch (e) {}
    if ((r.status === 400 || r.status === 401) && detail.indexOf('invalid_grant') !== -1) {
      const e = new Error('proxy invalid_grant'); e.invalidGrant = true; throw e;
    }
    const e = new Error('proxy non-conclusivo HTTP ' + r.status); e.transient = true; throw e;
  }

  async function refresh() {
    if (refreshPromise) return refreshPromise;
    const c = cfg();
    const rt = STORE.getItem(KEY_REFRESH);
    if (!rt) throw new Error('No refresh token');
    refreshPromise = (async () => {
      // 1) Prova il proxy backend (Safari-safe: la chiamata è al nostro dominio, non a shopify.com).
      try {
        return await refreshViaProxy(rt);
      } catch (e) {
        // Token davvero morto → inutile il fallback (fallirebbe uguale): propaga.
        if (e && e.invalidGrant) throw e;
        // Proxy non disponibile/non conclusivo → fallback al refresh diretto (comportamento storico).
        return await refreshDirect(rt, c);
      }
    })().finally(() => { refreshPromise = null; });
    return refreshPromise;
  }

  // ===== Refresh PROATTIVO =====
  // Rinnova il token PRIMA che scada (~30min) mentre la pagina è aperta → niente 401 a metà
  // sessione (che poi obbligava al re-login). Gira solo se c'è un refresh token e la tab è
  // visibile; refresh() è deduplicato via refreshPromise, quindi non moltiplica le chiamate.
  function startProactiveRefresh() {
    try {
      if (window.__wcProactiveRefresh) return;
      window.__wcProactiveRefresh = true;
    } catch (e) { return; }
    const PRE_MS = 180000; // rinnova quando mancano < 3 min alla scadenza
    async function tick() {
      try {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        const rt = STORE.getItem(KEY_REFRESH);
        const exp = Number(STORE.getItem(KEY_EXPIRES) || 0);
        if (rt && exp && (exp - Date.now() < PRE_MS)) {
          await refresh().catch(function () {});
        }
      } catch (e) {}
    }
    try { setInterval(tick, 60000); } catch (e) {}
    try {
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', function () {
          if (document.visibilityState === 'visible') tick();
        });
      }
    } catch (e) {}
    tick();
  }

  async function getAccessToken() {
    const tok = STORE.getItem(KEY_ACCESS);
    const exp = Number(STORE.getItem(KEY_EXPIRES) || 0);
    if (tok && exp && Date.now() < exp) return tok;
    if (STORE.getItem(KEY_REFRESH)) {
      try { return await refresh(); }
      catch (e) {
        // SOLO un refresh_token definitivamente invalido (400/401) azzera la sessione.
        // Un fallimento TRANSITORIO (timeout Safari/ITP, rete mobile lenta, 5xx) NON deve
        // cancellare i token: il vecchio logoutLocal() incondizionato distruggeva la sessione
        // al primo refresh lento su mobile → niente Bearer per sempre → 401 perenne (agenda
        // vuota da telefono). Tenendo i token, il tentativo successivo ri-prova e recupera.
        if (e && e.invalidGrant) logoutLocal();
        return null;
      }
    }
    return null;
  }

  function isLoggedIn() {
    return !!STORE.getItem(KEY_ACCESS);
  }

  function logoutLocal() {
    [KEY_ACCESS, KEY_REFRESH, KEY_EXPIRES, KEY_ID].forEach(k => STORE.removeItem(k));
    [KEY_VERIFIER, KEY_STATE, KEY_RETURN].forEach(k => PKCE_STORE.removeItem(k));
  }

  function logout(postLogoutRedirect) {
    const c = cfg();
    const idToken = STORE.getItem(KEY_ID);
    logoutLocal();
    const params = new URLSearchParams();
    if (idToken) params.set('id_token_hint', idToken);
    params.set('post_logout_redirect_uri', postLogoutRedirect || window.location.origin + '/');
    window.location.assign(`${c.logoutUrl}?${params.toString()}`);
  }

  // ===== Fetch wrapper con Authorization: Bearer =====
  async function authedFetch(url, init) {
    const token = await getAccessToken();
    const headers = new Headers((init || {}).headers || {});
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return fetch(url, Object.assign({}, init, { headers }));
  }

  // ===== Global fetch override per le chiamate al backend Wellcam =====
  // Solo URL che matchano WELLCAM_AUTH_CONFIG.apiOrigin (es. app.wellcam.fit)
  // ottengono automaticamente l'header Authorization. Le altre fetch sono invariate.
  // ===== Sessione cookie durevole (bulletproof mobile) =====
  // Quando c'è un token valido, chiediamo al backend di rilasciare un cookie HttpOnly su
  // app.wellcam.fit. Da lì la sessione sopravvive anche se Safari/ITP azzera localStorage
  // → login persistente su telefono. Additivo: se fallisce, resta tutto come prima (Bearer).
  // Ritorna true se ha AVVIATO il redirect top-level (la pagina sta per navigare).
  async function establishSessionCookie(returnToOverride) {
    try {
      if (window.__wcSessDone) return false;
      const apiOrigin = (window.WELLCAM_AUTH_CONFIG || {}).apiOrigin;
      if (!apiOrigin) return false;
      const token = await getAccessToken().catch(function () { return null; });
      if (!token) return false; // nessun token ora → si stabilirà al prossimo login
      const r = await fetch(apiOrigin + '/api/auth/session/establish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        credentials: 'include',
        body: '{}',
      });
      if (!r || !r.ok) return false;
      let j = null;
      try { j = await r.json(); } catch (e) {}
      // VERIFICA: il browser ha DAVVERO salvato il cookie? Safari/iOS può ignorare il
      // Set-Cookie ricevuto via fetch → controlliamo con una chiamata credenziale.
      let hasCookie = false;
      try {
        const ck = await fetch(apiOrigin + '/api/auth/session/check', { credentials: 'include' });
        if (ck && ck.ok) hasCookie = !!(await ck.json()).cookie;
      } catch (e) {}
      if (hasCookie) { window.__wcSessDone = true; return false; }
      // Cookie NON salvato (Safari): UNA navigazione top-level su app.wellcam.fit che
      // imposta il cookie first-party (accettato sempre) e torna subito qui. Guardia
      // anti-loop: max un hop ogni 30 minuti per tab.
      const hop = j && j.hop_code;
      if (!hop) return false;
      let last = 0;
      try { last = Number(sessionStorage.getItem('wc_sess.hop_ts') || '0'); } catch (e) {}
      if (Date.now() - last < 1800000) return false;
      try { sessionStorage.setItem('wc_sess.hop_ts', String(Date.now())); } catch (e) {}
      let ret = returnToOverride || (window.location.pathname + window.location.search + window.location.hash) || '/pages/area-cliente';
      if (ret.charAt(0) === '/') ret = window.location.origin + ret; // URL completo (host corrente whitelisted lato server)
      window.location.replace(apiOrigin + '/api/auth/session/redeem?c=' + encodeURIComponent(hop) + '&return_to=' + encodeURIComponent(ret));
      return true;
    } catch (e) { return false; }
  }

  function installFetchOverride() {
    const c = window.WELLCAM_AUTH_CONFIG || {};
    const apiOrigin = c.apiOrigin || 'https://app.wellcam.fit';
    if (window.__wcAuthFetchInstalled) return;
    window.__wcAuthFetchInstalled = true;
    const orig = window.fetch.bind(window);

    // Azzeramento della guardia re-login: una chiamata autenticata riuscita significa
    // che il token c'è → una futura scadenza potrà ri-tentare il re-login da zero.
    function clearReauthGuard() { try { sessionStorage.removeItem('wc_auth.reauth_ts'); } catch (e) {} }

    // Re-login SSO automatico quando il backend risponde 401 e NON abbiamo un JWT valido
    // (scaduto ~30min, strippato da Safari/ITP, login mai completato). L'utente ha già la
    // sessione storefront Shopify → il login SSO è seamless e torna col token. Con l'auth
    // email di backend disattivata, questa è l'unica via per non lasciare la dashboard rotta.
    // Sicurezze:
    //  - solo se esiste WELLCAM_CUSTOMER_EMAIL (sessione storefront presente);
    //  - SOLO nell'area app /pages/area-cliente (dashboard trainer + area cliente) → niente
    //    redirect indesiderati su carrello/checkout/prodotti/lezione-room;
    //  - guardia anti-loop: se abbiamo già ritentato < 90s fa e siamo ancora a 401, NON
    //    ripetiamo (la 401 arriva pulita all'app) → nessun ciclo login↔dashboard;
    //  - flag in-memory __wcReauthing per de-duplicare i 401 paralleli dello stesso load.
    function maybeReauth() {
      try {
        if (window.__wcReauthing) return;
        if (!window.WELLCAM_CUSTOMER_EMAIL) return;
        // Area app = dashboard trainer + area cliente. Servita SIA su /pages/area-cliente
        // SIA su /account (customers/account.json monta la stessa section): senza /account
        // qui, un trainer che sul telefono apre /account e va in 401 NON otteneva il
        // re-login automatico → agenda vuota da mobile, ok da PC (bug Giorgia 23/7).
        // /account/login|register non passano: lì manca WELLCAM_CUSTOMER_EMAIL (guardia sopra).
        if (!/^\/(pages\/area-cliente|account)(\/|$|#|\?)/i.test(window.location.pathname || '')) return;
        const now = Date.now();
        let last = 0;
        try { last = Number(sessionStorage.getItem('wc_auth.reauth_ts') || '0'); } catch (e) {}
        if (now - last < 90000) return;
        window.__wcReauthing = true;
        try { sessionStorage.setItem('wc_auth.reauth_ts', String(now)); } catch (e) {}
        login(window.location.pathname + window.location.search + window.location.hash);
      } catch (e) {}
    }

    window.fetch = async function(input, init) {
      init = init || {};
      let attachedBearer = false;
      let isApiCall = false;
      try {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        // Endpoint di AUTH-PLUMBING (refresh/token exchange/sessione): passthrough SENZA
        // await di getAccessToken. Il refresh via proxy passa da questo stesso override:
        // intercettarlo creava un'attesa CIRCOLARE (override → getAccessToken → refresh →
        // fetch → override → …) che stallava 7,8s e faceva partire le chiamate API senza
        // Bearer → 401 su mobile. Solo credentials per i cookie di sessione.
        if (url.indexOf(apiOrigin + '/api/auth/') === 0) {
          return orig(input, Object.assign({}, init, { credentials: 'include' }));
        }
        if (url.indexOf(apiOrigin) === 0) {
          isApiCall = true;
          // Race con timeout: getAccessToken NON deve mai bloccare la richiesta API.
          // 7.8s: deve superare il timeout interno del refresh (7s) così, quando il
          // refresh su mobile è lento ma RIESCE, il Bearer fresco fa in tempo ad attaccarsi
          // (prima con 4.5s la race tagliava il refresh a metà → nessun Bearer → 401).
          // Se il refresh si appende davvero (Safari ITP), la race sblocca comunque a 7.8s.
          const token = await Promise.race([
            getAccessToken().catch(function () { return null; }),
            new Promise(function (resolve) { setTimeout(function () { resolve(null); }, 7800); }),
          ]);
          const headers = new Headers(init.headers || (input && input.headers) || {});
          if (token && !headers.has('Authorization')) {
            headers.set('Authorization', `Bearer ${token}`);
            attachedBearer = true;
          }
          // FALLBACK Safari/ITP: se NON c'è un Bearer (token SSO appeso → null su Safari),
          // iniettiamo X-Customer-Email se la pagina lo espone, così TUTTE le chiamate API
          // (anche quelle che non passano header propri, es. /oauth/google/status) hanno
          // comunque l'auth via email. Senza, su Safari restavano senza auth → 401.
          if (!attachedBearer && !headers.has('X-Customer-Email') && window.WELLCAM_CUSTOMER_EMAIL) {
            headers.set('X-Customer-Email', window.WELLCAM_CUSTOMER_EMAIL);
          }
          // credentials:'include' → invia/riceve il cookie di sessione wc_sess (same-site
          // app.wellcam.fit ↔ beta.wellcam.fit). È ciò che tiene vivo il login su mobile
          // Safari quando localStorage viene azzerato da ITP. Il Bearer resta primario.
          init = Object.assign({}, init, { headers, credentials: 'include' });
        }
      } catch {}
      const res = await orig(input, init);
      if (!isApiCall || !res) return res;

      if (res.status === 401) {
        // Case A — avevamo un Bearer ma il backend l'ha rifiutato (token scaduto, chiave
        // ruotata, aud non combaciante, clock skew…): lo buttiamo e ritentiamo UNA volta
        // senza Authorization. Idempotente via __wcRetriedNoBearer.
        if (attachedBearer && !init.__wcRetriedNoBearer) {
          try { logoutLocal(); } catch {}
          let headers2;
          try { headers2 = new Headers(init.headers || {}); headers2.delete('Authorization'); }
          catch { headers2 = undefined; }
          const retry = await orig(input, Object.assign({}, init, { headers: headers2, __wcRetriedNoBearer: true }));
          if (retry && retry.status === 401) maybeReauth(); // ancora 401 → JWT davvero assente → re-login SSO
          else if (retry && retry.status >= 200 && retry.status < 300) clearReauthGuard();
          return retry;
        }
        // Case B — nessun Bearer valido (o retry già fatto) e ancora 401: la sessione non ha
        // un JWT. Innesca il re-login SSO (una volta, guardato) per ottenerne uno fresco.
        maybeReauth();
        return res;
      }

      // Chiamata autenticata riuscita → azzera la guardia re-login.
      if (res.status >= 200 && res.status < 300) clearReauthGuard();
      return res;
    };

    // Tiene viva la sessione mentre la pagina è aperta: rinnova il token prima che scada.
    startProactiveRefresh();
    // Rilascia il cookie di sessione durevole (bulletproof mobile Safari/ITP).
    establishSessionCookie();
  }

  window.WellcamAuth = {
    login,
    handleCallback,
    logout,
    logoutLocal,
    getAccessToken,
    isLoggedIn,
    authedFetch,
    installFetchOverride,
    startProactiveRefresh,
  };
})();
